// ─── Rundnachrichten ─────────────────────────────────────────────────────────
//
// Nachrichten des Betreibers an alle seine Kunden – eine angekündigte Wartung,
// eine Störung, ein Hinweis auf Neuerungen.
//
// Der Unterschied zwischen den beiden Arten ist keine Kosmetik, sondern der
// Grund, warum es überhaupt zwei gibt:
//
//   * Eine BETRIEBSNACHRICHT geht an jeden. Wer erfährt, dass sein Zugang am
//     Sonntag zwei Stunden stillsteht, braucht das, ob er will oder nicht –
//     und wer sich davon abmelden könnte, stünde am Sonntag vor einer
//     unerklärlichen Fehlermeldung.
//   * Eine MITTEILUNG ist alles Übrige: neue Funktionen, Tipps, Hinweise.
//     Davon kann sich jeder abmelden, und der Schalter dafür steht in seinen
//     eigenen Kontoeinstellungen, nicht beim Betreiber.
//
// Die Trennung sauber zu halten liegt beim Betreiber. Eine Werbemail als
// „Betriebsnachricht" zu verschicken wäre technisch möglich – und ein
// Wortbruch gegenüber dem Kunden, der genau diesen Schalter umgelegt hat.

export type RundnachrichtArt = 'betrieb' | 'info';

export const RUNDNACHRICHT_ARTEN: readonly RundnachrichtArt[] = ['betrieb', 'info'];

export interface ArtBeschreibung {
  art: RundnachrichtArt;
  label: string;
  /** Was das für den Empfänger bedeutet. */
  bedeutung: string;
  /** Ob eine Abmeldung sie aufhält. */
  abmeldbar: boolean;
}

export const ART_BESCHREIBUNGEN: readonly ArtBeschreibung[] = [
  {
    art: 'betrieb',
    label: 'Betriebsnachricht',
    bedeutung:
      'Wartung, Störung, Sicherheit – geht an jedes aktive Konto, auch an abgemeldete.',
    abmeldbar: false,
  },
  {
    art: 'info',
    label: 'Mitteilung',
    bedeutung:
      'Neuerungen und Hinweise – wer sich abgemeldet hat, bekommt sie nicht.',
    abmeldbar: true,
  },
];

export function istRundnachrichtArt(wert: unknown): wert is RundnachrichtArt {
  return typeof wert === 'string' && (RUNDNACHRICHT_ARTEN as string[]).includes(wert);
}

/** Der Stand einer Rundnachricht. */
export type RundnachrichtStatus = 'laeuft' | 'fertig' | 'abgebrochen';

export interface Empfaengerkreis {
  /** Nur Konten mit diesem Tarif. null = alle Tarife. */
  planId: string | null;
  /** Nur Konten, denen mindestens ein Stammbaum gehört. */
  nurBesitzer: boolean;
}

export interface Rundnachricht {
  id: string;
  art: RundnachrichtArt;
  betreff: string;
  text: string;
  /** Ziel der Schaltfläche unter dem Text – null, wenn keine gesetzt war. */
  linkUrl: string | null;
  /** Beschriftung der Schaltfläche. Leer bedeutet „Jetzt ansehen". */
  linkText: string | null;
  planId: string | null;
  /** Der Tarifname zum Zeitpunkt des Versands – der Tarif kann später weg sein. */
  planName: string | null;
  nurBesitzer: boolean;
  status: RundnachrichtStatus;
  empfaenger: number;
  zugestellt: number;
  fehler: number;
  fehlerText: string | null;
  erstelltVon: string | null;
  erstelltAm: string;
  beendetAm: string | null;
}

/** Was eine Vorschau über den Empfängerkreis sagt. */
export interface EmpfaengerVorschau {
  /** Wie viele Konten die Nachricht bekämen. */
  anzahl: number;
  /** Wie viele davon durch eine Abmeldung wegfallen (nur bei „Mitteilung"). */
  abgemeldet: number;
  /** Ein paar Adressen als Stichprobe – zum Erkennen grober Fehlgriffe. */
  beispiele: string[];
}
